import { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useFeature } from '@growthbook/growthbook-react';
import { useSelector } from 'react-redux';
import { useIsDesktopLayout } from '../../../../hooks/useMediaQuery';
import { usePromoBonusesPageRedirectCallback } from '../../../../hooks/usePromoBonusSystemRedirects';
import { useIsUnifiedPromoBonusSystemEnabled } from '../../../../hooks/useIsUnifiedPromoBonusSystemEnabled';
import { useIsSSCOMBrand } from '../../../../hooks/useIsSSCOMBrand';
import { isLoggedInSelector } from '../../../../redux/reducers/app/params';
import { useIsFavouriteGamesEnabled } from '../../../../hooks/useIsFavouriteGamesEnabled';
import { useBonusesDataSelector } from '../../../../hooks/API/useQueryBonuses';
import {
  freeSpinBonusSelector,
  wageringBonusSelector,
} from '../../../../hooks/API/selectors/bonuses';
import { sendAnalyticsData } from '../../../../services/analytics';
import type {
  MainNavigationItem,
  MainNavigationPredicates,
  NavigationItem,
} from '../types';
import FEATURES from '../../../../constants/featureToggling';
import { FREE_SPINS_NAVIGATION_ITEM_ID, navigationItems } from '../constants';
import { NAMESPACES } from '../../../../constants/namespaces';
import {
  ANALYTICS_SCHEMA_URLS,
  ANALYTICS_SCREEN_AREAS,
} from '../../../../constants/analytics';
import {
  COLLECTING,
  FREE_SPINS_BONUS_MECHANIC,
  PENDING,
} from '../../../../constants/bonuses';
import {
  BONUSES_LIST,
  TOURNAMENTS,
} from '../../../../constants/routeTemplates';
import { getGameDetailRoute } from '../../../../utils/routes';
import {
  useTournamentLobbyAvailable,
  useTournamentLobbyRedirectCallback,
} from '../../../../hooks/useTournamentLobbyRedirect';

const useMainNavigationPredicates = (): MainNavigationPredicates => {
  const isDesktop = useIsDesktopLayout();
  const isSCCOM = useIsSSCOMBrand();

  const isUnifiedPromoBonusSystemEnabled =
    useIsUnifiedPromoBonusSystemEnabled();

  const isLoggedIn = useSelector(isLoggedInSelector);
  const isEnabledFavoriteGames = useIsFavouriteGamesEnabled();
  const isEnabledBonusesSystem = useFeature(FEATURES.CAN_USE_BONUS).on;
  const isTournamentLobbyAvailable = useTournamentLobbyAvailable();

  const wageringBonus = useBonusesDataSelector(wageringBonusSelector);
  const freeSpinBonus = useBonusesDataSelector(freeSpinBonusSelector);

  const isWageringGamesAvailable = Boolean(wageringBonus);
  const shouldDisplayFreeSpinsButton =
    (freeSpinBonus?.status === PENDING &&
      freeSpinBonus?.mechanicType === FREE_SPINS_BONUS_MECHANIC) ||
    freeSpinBonus?.status === COLLECTING;

  return useMemo(
    () => ({
      isDesktop,
      isSCCOM,
      isUnifiedPromoBonusSystemEnabled,
      isLoggedIn,
      isEnabledFavoriteGames,
      isEnabledBonusesSystem,
      isWageringGamesAvailable,
      shouldDisplayFreeSpinsButton,
      isTournamentLobbyAvailable,
    }),
    [
      isDesktop,
      isSCCOM,
      isUnifiedPromoBonusSystemEnabled,
      isLoggedIn,
      isEnabledFavoriteGames,
      isEnabledBonusesSystem,
      isWageringGamesAvailable,
      shouldDisplayFreeSpinsButton,
      isTournamentLobbyAvailable,
    ]
  );
};

const useMainNavigationLinkPathBuilderMap = () => {
  const freeSpinBonus = useBonusesDataSelector(freeSpinBonusSelector);

  return useMemo(
    () => ({
      [FREE_SPINS_NAVIGATION_ITEM_ID]: () =>
        getGameDetailRoute(freeSpinBonus?.lobbyGameId),
    }),
    [freeSpinBonus]
  );
};

const useTransformMainInfoToNavigationItemCallback = () => {
  const predicates = useMainNavigationPredicates();
  const onItemClickMap = useMainNavigationOnItemClickMap();
  const linkPathBuilderMap = useMainNavigationLinkPathBuilderMap();

  return useCallback(
    (items: MainNavigationItem[]): NavigationItem[] => {
      return (
        items
          .filter((item) =>
            item?.predicateResolver ? item.predicateResolver(predicates) : true
          )
          .map(
            ({
              Icon,
              analyticsData,
              translationKey,
              linkPathBuilder: itemLinkPathBuilder,
              activeRoutes,
              productType,
              rightCellContent,
              id,
            }) => {
              const onItemClick = onItemClickMap[id] || onItemClickMap.default;
              const linkPathBuilder =
                linkPathBuilderMap[id] || itemLinkPathBuilder;
              return {
                translationKey,
                Icon,
                onItemClick: ({ analyticsData }) =>
                  onItemClick(linkPathBuilder(), analyticsData),
                activeRoutes: activeRoutes ? activeRoutes : [],
                linkPathBuilder,
                namespace: NAMESPACES.PAGES,
                rightCellContent,
                productType,
                id: linkPathBuilder(),
                analyticsData: {
                  ...analyticsData,
                  screen_area: ANALYTICS_SCREEN_AREAS.SIDEBAR,
                  schema: ANALYTICS_SCHEMA_URLS.NAVIGATION_CLICK,
                },
              };
            }
          ) || []
      );
    },
    [onItemClickMap, predicates, linkPathBuilderMap]
  );
};

const useMainNavigationOnItemClickMap = () => {
  const history = useHistory();

  const onItemClickDefault = useCallback(
    (path: string, analyticsData: Record<string, any>) => {
      if (analyticsData) {
        sendAnalyticsData(analyticsData);
      }

      history.push(path);
    },
    [history]
  );

  const isUnifiedBonusSystemEnabled = useIsUnifiedPromoBonusSystemEnabled();
  const redirectToBonusList = usePromoBonusesPageRedirectCallback();
  const redirectToTournamentsLobby = useTournamentLobbyRedirectCallback();

  const onBonusesClick = useCallback(() => {
    redirectToBonusList();
  }, [redirectToBonusList]);

  const onTournamentsClick = useCallback(() => {
    redirectToTournamentsLobby();
  }, [redirectToTournamentsLobby]);

  return useMemo(
    () => ({
      ...(isUnifiedBonusSystemEnabled
        ? { [BONUSES_LIST]: onBonusesClick }
        : {}),
      [TOURNAMENTS]: onTournamentsClick,
      default: onItemClickDefault,
    }),
    [
      onBonusesClick,
      onTournamentsClick,
      isUnifiedBonusSystemEnabled,
      onItemClickDefault,
    ]
  );
};

export const useMainNavigation = (): NavigationItem[] => {
  const transformer = useTransformMainInfoToNavigationItemCallback();
  return transformer(navigationItems);
};
